import * as React from 'react';
import { Card, CardContent } from './Card';
import { Icon } from './icons/Icon';
import { Tooltip } from './Tooltip';
import { cn } from '../utils';
import type { IconName } from '../icons/names';

// ---------------------------------------------------------------------------
// Icon tile
// ---------------------------------------------------------------------------

export interface DocumentIconTileProps {
  /** Icon drawn on the tile when no `svg` is given. */
  icon?: IconName;
  /** Custom SVG drawn instead of `icon`. */
  svg?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Tile background, e.g. `bg-background-muted` or a kind's tint. */
  tileClass?: string;
  /** Icon colour. */
  iconClass?: string;
  className?: string;
  testId?: string;
  id?: string;
}

/** The tinted square with a document's icon that opens a list row. */
const DocumentIconTile = React.forwardRef<HTMLDivElement, DocumentIconTileProps>(
  (
    {
      icon = 'file-lines',
      svg: Svg,
      tileClass = 'bg-background-muted',
      iconClass = 'text-foreground-base',
      className,
      testId,
      id
    },
    ref
  ) => (
    <div
      ref={ref}
      id={id}
      data-testid={testId}
      className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
        tileClass,
        className
      )}
    >
      {Svg ? (
        <Svg className={cn('h-5 w-5', iconClass)} aria-hidden='true' />
      ) : (
        <Icon name={icon} size='lg' className={iconClass} />
      )}
    </div>
  )
);
DocumentIconTile.displayName = 'DocumentIconTile';

// ---------------------------------------------------------------------------
// Slot types
// ---------------------------------------------------------------------------

/** A sub line: a string gets the row's muted style, any other node renders as given. */
export type DocumentListItemLine = React.ReactNode;

export type DocumentListItemDateTone = 'default' | 'error';

/** One label/value pair of the dates column. */
export interface DocumentListItemDate {
  /** Shown with a trailing colon. */
  label: React.ReactNode;
  value: React.ReactNode;
  /** `error` marks the value, e.g. an overdue due date. */
  tone?: DocumentListItemDateTone;
  /** Test id of the value. */
  testId?: string;
}

/** Who the document is between, laid out like an invoice's supplier → customer. */
export interface DocumentListItemParty {
  /** The supplier's name. */
  name?: React.ReactNode;
  /** The supplier's VAT id, under the name. */
  vatId?: React.ReactNode;
  /** The other side, shown after an arrow. */
  counterparty?: React.ReactNode;
}

export interface DocumentListItemAmount {
  value: React.ReactNode;
  lines?: DocumentListItemLine[];
  /** Test id of the whole amount column. */
  testId?: string;
}

export interface DocumentListItemProps extends Omit<React.ComponentProps<typeof Card>, 'children'> {
  /** Selection control in front of the row; its clicks do not reach `onClick`. */
  checkbox?: React.ReactNode;
  /** Keeps an empty checkbox column on a wide row, so it lines up with selectable rows. */
  reserveCheckbox?: boolean;
  /** The kind's tile, usually a `DocumentIconTile`. */
  icon?: React.ReactNode;
  /** Tooltip of the icon. */
  iconLabel?: string;
  number: React.ReactNode;
  /** Secondary number beside the main one, from the wide layout up. */
  numberAside?: React.ReactNode;
  /** Kind or type under the number, optionally followed by who and where; truncates. */
  typeLabel?: React.ReactNode;
  party?: DocumentListItemParty;
  dates?: DocumentListItemDate[];
  /** First badge, shown at its own size in both layouts. */
  leadingBadge?: React.ReactNode;
  /** Already translated badges, status first; they take the compact size on a narrow row. */
  badges?: React.ReactNode;
  /** Shown after the badges, in the free space of their row — e.g. who approves. */
  badgesAside?: React.ReactNode;
  /** Line under the badges; a string is truncated, a node renders as a wrapping line. */
  description?: React.ReactNode;
  amount?: DocumentListItemAmount;
}

const isText = (node: React.ReactNode): node is string | number =>
  typeof node === 'string' || typeof node === 'number';

const presentLines = (lines?: DocumentListItemLine[]): DocumentListItemLine[] =>
  (lines ?? []).filter(line => line != null && line !== false && line !== '');

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

function IconWithLabel({ icon, label }: { icon: React.ReactNode; label?: string }) {
  if (!icon) return null;
  if (!label) return <>{icon}</>;
  return (
    <Tooltip position='top' text={label} trigger='hover'>
      {icon}
    </Tooltip>
  );
}

function AmountLines({ lines }: { lines?: DocumentListItemLine[] }) {
  return (
    <>
      {presentLines(lines).map((line, index) =>
        isText(line) ? (
          <span key={index} className='text-xs text-foreground-muted whitespace-nowrap'>
            {line}
          </span>
        ) : (
          <React.Fragment key={index}>{line}</React.Fragment>
        )
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop layout
// ---------------------------------------------------------------------------

function DocumentListItemDesktop({
  checkbox,
  reserveCheckbox,
  icon,
  iconLabel,
  number,
  numberAside,
  typeLabel,
  party,
  dates,
  leadingBadge,
  badges,
  badgesAside,
  description,
  amount
}: DocumentListItemProps) {
  return (
    <div className='hidden @2xl/document-row:flex items-center gap-md @4xl/document-row:gap-lg w-full overflow-hidden'>
      {checkbox && (
        <div className='shrink-0' onClick={stopPropagation}>
          {checkbox}
        </div>
      )}
      {/* As wide as the checkbox, so the columns sit where a selectable row's do. */}
      {!checkbox && reserveCheckbox && <div className='shrink-0 w-4' aria-hidden='true' />}
      {/* Col 1 – icon */}
      <IconWithLabel icon={icon} label={iconLabel} />

      {/* Col 2 – number + aside + type */}
      {/* Width is a target, not a floor: the labels inside truncate, so the column may give
          way under pressure rather than pushing the row past its box. */}
      <div className='min-w-0 flex flex-col w-44 @4xl/document-row:w-52 @5xl/document-row:w-60'>
        <div className='flex items-center gap-sm mb-2xs'>
          <span className='font-semibold text-foreground-base truncate'>{number}</span>
          {numberAside && (
            <span className='text-xs text-foreground-muted whitespace-nowrap hidden @4xl/document-row:inline'>
              {numberAside}
            </span>
          )}
        </div>
        {typeLabel && <span className='text-xs text-foreground-muted truncate'>{typeLabel}</span>}
      </div>

      {/* Col 3 – party, appears once the row is wide enough */}
      <div className='hidden @4xl/document-row:flex flex-col min-w-0 flex-1 max-w-44 @5xl/document-row:max-w-52'>
        {party?.name && <span className='text-sm text-foreground-base truncate'>{party.name}</span>}
        {party?.vatId && (
          <span className='text-xs text-foreground-muted truncate'>{party.vatId}</span>
        )}
        {party?.counterparty && (
          <div className='flex items-center gap-2xs min-w-0'>
            <Icon name='arrow-right' className='h-2.5 w-2.5 text-foreground-muted shrink-0' />
            <span className='text-sm text-foreground-muted truncate'>{party.counterparty}</span>
          </div>
        )}
      </div>

      {/* Col 4 – dates */}
      <div className='grid grid-cols-[auto_1fr] items-center gap-x-sm shrink-0 w-36 @4xl/document-row:w-40'>
        {dates?.map((date, index) => (
          <React.Fragment key={index}>
            <span className='text-xs text-foreground-muted whitespace-nowrap'>{date.label}:</span>
            <span
              data-testid={date.testId}
              className={cn(
                'text-sm whitespace-nowrap',
                date.tone === 'error' ? 'text-error-text font-semibold' : 'text-foreground-base'
              )}
            >
              {date.value}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Col 5 – badges (one row) + description underneath */}
      <div className='flex flex-col gap-2xs min-w-0 flex-1'>
        <div className='flex items-center gap-xs min-w-0'>
          <div className='flex items-center gap-xs flex-wrap shrink-0 max-w-full'>
            {leadingBadge}
            {badges}
          </div>
          {/* Stays beside the badges and truncates rather than wrapping under them. */}
          {badgesAside && <div className='min-w-0 flex'>{badgesAside}</div>}
        </div>
        {description &&
          (isText(description) ? (
            <span className='text-xs text-foreground-muted truncate'>{description}</span>
          ) : (
            <div className='flex items-center gap-x-md gap-y-2xs flex-wrap min-w-0 text-xs text-foreground-muted'>
              {description}
            </div>
          ))}
      </div>

      {/* Col 6 – amount */}
      <div className='flex flex-col items-end ml-auto shrink-0' data-testid={amount?.testId}>
        {amount && (
          <>
            <span className='text-base @4xl/document-row:text-lg font-semibold text-foreground-base whitespace-nowrap'>
              {amount.value}
            </span>
            <AmountLines lines={amount.lines} />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile layout
// ---------------------------------------------------------------------------

function DocumentListItemMobile({
  checkbox,
  icon,
  iconLabel,
  number,
  typeLabel,
  party,
  dates,
  leadingBadge,
  badges,
  badgesAside,
  description,
  amount
}: DocumentListItemProps) {
  const hasParty = Boolean(party?.name || party?.counterparty || party?.vatId);

  // Hides exactly where the desktop row appears — a viewport breakpoint here would
  // leave a narrow row inside a wide window with neither layout rendered.
  return (
    <div className='@2xl/document-row:hidden'>
      {/* Row 1 – number, amount */}
      <div className='flex items-start gap-md mb-2xs'>
        {checkbox && (
          <div className='shrink-0 self-center' onClick={stopPropagation}>
            {checkbox}
          </div>
        )}
        <IconWithLabel icon={icon} label={iconLabel} />
        <div className='flex-1 flex items-start justify-between gap-sm min-w-0'>
          <span className='font-bold text-foreground-base truncate'>{number}</span>
          {amount && (
            <div className='flex flex-col items-end shrink-0' data-testid={amount.testId}>
              <span className='font-bold text-foreground-base whitespace-nowrap'>
                {amount.value}
              </span>
              <AmountLines lines={amount.lines} />
            </div>
          )}
        </div>
      </div>

      {/* Row 2 – document type label */}
      {typeLabel && (
        <div className='flex items-center gap-sm text-xs text-foreground-muted mb-sm'>
          <span>{typeLabel}</span>
        </div>
      )}

      {/* Row 3 – party → counterparty, VAT id underneath */}
      {hasParty && (
        <div className='mb-sm min-w-0'>
          {(party?.name || party?.counterparty) && (
            <div className='flex items-center gap-sm text-sm min-w-0'>
              {party?.name && <span className='text-foreground-base truncate'>{party.name}</span>}
              {party?.counterparty && (
                <>
                  <Icon name='arrow-right' className='h-3 w-3 text-foreground-muted shrink-0' />
                  <span className='text-foreground-muted truncate'>{party.counterparty}</span>
                </>
              )}
            </div>
          )}
          {party?.vatId && (
            <span className='block text-xs text-foreground-muted truncate'>{party.vatId}</span>
          )}
        </div>
      )}

      {/* Row 4 – dates as label/value grid so they never overflow on narrow screens */}
      {dates && dates.length > 0 && (
        <div className='grid grid-cols-[auto_1fr] gap-x-sm gap-y-2xs mb-md text-sm'>
          {dates.map((date, index) => (
            <React.Fragment key={index}>
              <span className='text-foreground-muted whitespace-nowrap'>{date.label}:</span>
              <span
                data-testid={date.testId}
                className={cn(
                  date.tone === 'error' ? 'text-error-text font-semibold' : 'text-foreground-base'
                )}
              >
                {date.value}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Row 5 – badges + description underneath */}
      <div className='flex flex-col gap-sm'>
        <div className='flex items-center gap-sm flex-wrap'>
          {/* Badges take the compact size here, so they fit beside each other. */}
          {leadingBadge}
          {badges && <span className='contents *:text-2xs *:px-sm'>{badges}</span>}
          {badgesAside && <div className='min-w-0 max-w-full'>{badgesAside}</div>}
        </div>
        {description &&
          (isText(description) ? (
            <span className='text-sm text-foreground-muted line-clamp-2'>{description}</span>
          ) : (
            <div className='flex items-center gap-x-md gap-y-2xs flex-wrap min-w-0 text-sm text-foreground-muted'>
              {description}
            </div>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * One row of a document list: icon, number and type, party, dates, badges with a description,
 * and the amount. Empty columns keep their width so the rows of one list align.
 */
const DocumentListItem = React.forwardRef<HTMLDivElement, DocumentListItemProps>(
  (
    {
      checkbox,
      reserveCheckbox,
      icon,
      iconLabel,
      number,
      numberAside,
      typeLabel,
      party,
      dates,
      leadingBadge,
      badges,
      badgesAside,
      description,
      amount,
      onClick,
      className,
      testId,
      id,
      ...props
    },
    ref
  ) => {
    const slots = {
      checkbox,
      reserveCheckbox,
      icon,
      iconLabel,
      number,
      numberAside,
      typeLabel,
      party,
      dates,
      leadingBadge,
      badges,
      badgesAside,
      description,
      amount
    };
    return (
      <Card
        ref={ref}
        id={id}
        testId={testId}
        className={cn(
          // A query container: the row has to lay itself out against its own width, not the
          // viewport's — side panels (filters, AI chat) shrink it without shrinking the window.
          '@container/document-row w-full',
          onClick && 'cursor-pointer hover:shadow-md transition-shadow',
          className
        )}
        onClick={onClick}
        {...props}
      >
        <CardContent className='p-md @2xl/document-row:p-lg'>
          <DocumentListItemMobile {...slots} />
          <DocumentListItemDesktop {...slots} />
        </CardContent>
      </Card>
    );
  }
);
DocumentListItem.displayName = 'DocumentListItem';

export { DocumentListItem, DocumentIconTile };
